import { createContext, useContext, useState, type ReactNode } from "react";

interface LoadingOverlayContextValue {
  showLoading: (msg?: string, subMsg?: string) => void;
  hideLoading: () => void;
  isLoading: boolean;
}

const LoadingOverlayContext = createContext<LoadingOverlayContextValue | null>(null);

export function LoadingOverlayProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("Generating PDF Report...");
  const [subMessage, setSubMessage] = useState(
    "Please wait while we create your document",
  );

  const showLoading = (msg?: string, subMsg?: string) => {
    if (msg) setMessage(msg);
    if (subMsg) setSubMessage(subMsg);
    setIsLoading(true);
  };

  const hideLoading = () => {
    setIsLoading(false);
  };

  return (
    <LoadingOverlayContext.Provider value={{ showLoading, hideLoading, isLoading }}>
      {children}
      {isLoading && (
        <LoadingOverlayUI message={message} subMessage={subMessage} />
      )}
    </LoadingOverlayContext.Provider>
  );
}

export function useLoadingOverlay(): LoadingOverlayContextValue {
  const context = useContext(LoadingOverlayContext);
  if (!context) {
    throw new Error(
      "useLoadingOverlay must be used within a LoadingOverlayProvider",
    );
  }
  return context;
}

function LoadingOverlayUI({
  message,
  subMessage,
}: {
  message: string;
  subMessage: string;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 text-center max-w-sm mx-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2f5241] mx-auto mb-4" />
        <p className="text-gray-700 text-lg font-semibold">{message}</p>
        <p className="text-gray-500 text-sm mt-2">{subMessage}</p>
      </div>
    </div>
  );
}

export default function LoadingOverlay() {
  return null;
}
